<template>
    <v-container class="pa-4">
        <v-card max-width="700" elevation="0">
            <v-row no-gutters>
                <v-col cols="4">
                    <v-img
                        :src="campgroundImage"
                        aspect-ratio="1.5"
                        cover
                    ></v-img>
                </v-col>
                <v-col cols="8" class="d-flex flex-column justify-center pa-4">
                    <v-card-title class="text-h6"> Item Database </v-card-title>
                    <v-card-text>
                        All items found in the world with their attributes,
                        crafting recipe and others.
                    </v-card-text>
                </v-col>
            </v-row>
        </v-card>
        <v-row class="mb-2">
            <v-col class="d-flex justify-end">
                <v-btn color="primary" @click="createNewItem"> New Item </v-btn>
            </v-col>
        </v-row>
    </v-container>

    <v-data-table
        density="compact"
        :headers="headers"
        :items="items"
        item-value="id"
        @click:row="handleRowClick"
        :items-per-page="50"
        :items-per-page-options="[10, 25, 50, 100]"
    >
        <template #[`item.bPlaceable`]="{ item }">
            {{ item.bPlaceable ? 'yes' : '' }}
        </template>

        <template #[`item.bIsContainer`]="{ item }">
            {{ item.bIsContainer ? 'yes' : '' }}
        </template>

        <template #[`item.maxStackSize`]="{ item }">
            {{ item.bIsStackable ? item.maxStackSize : '' }}
        </template>

        <template #[`item.weight`]="{ item }">
            {{ Number(item.weight).toFixed(2) }}
        </template>

        <template #[`item.actions`]="{ item }">
            <v-icon size="small" class="me-2" @click="editItem(item)"
                >mdi-pencil</v-icon
            >
            <v-icon size="small" class="me-2" @click.stop="deleteItem(item)"
                >mdi-trash-can</v-icon
            >
        </template>

        <template #[`item.visibility`]="{ item }">
            {{ visibilityLabels[item.visibility] || item.visibility }}
        </template>

        <template #[`item.name`]="{ item }">
            <div class="d-flex align-center">
                <img
                    v-if="item.iconData"
                    :src="`data:image/png;base64,${item.iconData}`"
                    alt="icon"
                    width="32"
                    height="32"
                    class="mr-2"
                />
                <span>{{ item.name }}</span>
            </div>
        </template>

    </v-data-table>
    
    <edit-item 
        v-model="dialogEdit" 
        :item="editingItem" 
        @request-reload="reload"
        />

    <v-dialog v-model="dialogDelete" max-width="400">
        <v-card>
            <v-card-title class="text-h6">Are you sure?</v-card-title>
            <v-card-text>This action cannot be undone.</v-card-text>
            <v-card-actions class="justify-end">
                <v-btn text @click="cancelDelete">Cancel</v-btn>
                <v-btn color="error" text @click="confirmDelete"
                    >Yes, Delete</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import EditItem from './EditItem.vue';
import campgroundImage from '@/assets/lowpoly-items-campground.png';

export default {
    data() {
        return {
            isBusy: false,
            campgroundImage,
            items: [],
            editingItem: null,
            deletingItem: null,
            dialogEdit: false,
            dialogDelete: false,
            headers: [
                { title: 'Name', key: 'name' },
                { title: 'Description', key: 'description' },
                { title: 'Handle', key: 'itemHandle' },
                { title: 'Type', key: 'itemType' },
                { title: 'Item Quality', key: 'itemQuality' },
                { title: 'Rarity', key: 'rarity' },
                { title: 'Weight', key: 'weight' },
                { title: 'Visibility', key: 'visibility' },
                { title: 'Placeable', key: 'bPlaceable' },
                { title: 'Container', key: 'bIsContainer' },
                { title: 'Max Stack', key: 'maxStackSize' },
                { title: 'Created', key: 'createdAt' },
                { title: 'Updated', key: 'updatedAt' },
                { title: 'Actions', key: 'actions', sortable: false },
            ],
            visibilityLabels: {
                'EIV_None': 'None',
                'EIV_Floor': 'Floor',
                'EIV_Translucid': 'Translucid',
                'EIV_SeeThrough': 'See Through'
            }
        };
    },
    components: {
        EditItem,
    },
    mounted() {
        this.reload();
    },
    methods: {
        reload() {
            console.log('Retrieving items...');

            fetch(`${process.env.VUE_APP_STONECREEK_BACKEND}/api/v2/items`)
                .then((response) => {
                    if (!response.ok) throw new Error('Fetch failed');

                    return response.json();
                })
                .then((json) => {
                    this.items = json.items.map((item) => ({
                        ...item,
                        craftable: item.craftable === null
                            ? ''
                            : typeof item.craftable === 'object'
                                ? JSON.stringify(item.craftable, null, 2)
                                : '',
                        itemStatistics: item.itemStatistics === null
                            ? ''
                            : typeof item.itemStatistics === 'object'
                                ? JSON.stringify(item.itemStatistics, null, 2)
                                : '',
                    }));
                })
                .then((json) => {
                    return fetch(`${process.env.VUE_APP_STONECREEK_BACKEND}/api/v2/items/icons`);
                })
                .then((response) => {
                    if (!response.ok) throw new Error('Icon fetch failed');

                    return response.json();
                })
                .then((json) => {

                    const all_icons = new Map();

                    json.items.forEach((icon) => {
                        all_icons.set(icon.iconHandle, icon.iconData);
                    });

                    this.items.forEach((item) => {

                        if (all_icons.has(item.itemHandle))
                            item.iconData = all_icons.get(item.itemHandle);
                        else
                            console.error("!!! icon NOT found:" + item.itemHandle);
                    });
                })
                .catch((error) => {
                    console.error('Failed to load items:', error);
                });
        },
        handleRowClick(event, item) {
            this.editingItem = item.item;
            this.dialogEdit = true;
        },
        editItem(item) {
            this.editingItem = item;
            this.dialogEdit = true;
        },
        deleteItem(item) {
            this.deletingItem = item;
            this.dialogDelete = true;
        },
        cancelDelete() {
            this.deletingItem = null;
            this.dialogDelete = false;
        },
        confirmDelete() {
            const token = localStorage.getItem('Auth');
            this.isBusy = true;
            fetch(
                `${process.env.VUE_APP_STONECREEK_BACKEND}/api/v2/item/${this.deletingItem.id}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                if (!response.ok) throw new Error('Fetch failed');

                this.isBusy = false;
                this.deletingItem = null;
                this.dialogDelete = false;

                this.reload();
            })
            .catch((error) => {
                console.error('Failed to load items:', error);
            });
        },
        closeDialog() {
            this.editingItem = null;
            this.dialogEdit = false;
        },
        createNewItem() {
            this.editingItem = {};
            this.dialogEdit = true;
        },
        saveItem() {
            this.closeDialog();
        },
    },
};
</script>